import { TRACK_STATE_COLORS, BUSINESS_TYPE_COLORS } from './mapStyle'

interface TierRow { color: string; label: string }

interface MapLegendProps {
  style?: React.CSSProperties
  /** Optional mono uppercase eyebrow above the rows (Aurora+ `.ml-title`, s21). */
  title?: string
  tiers?: TierRow[]
  /** Show all pin-state rows — identical on every page. Overrides individual show* props. */
  showAll?: boolean
  showOwnLocation?: boolean
  showStorefront?: boolean
  showDelivery?: boolean
  showCluster?: boolean
  showProspect?: boolean
  showTracking?: boolean
  showBlocked?: boolean
  /** Tracking AND blocked simultaneously — yellow inner dot. */
  showBoth?: boolean
  /** Blocked by a competitor (incoming) — amber ring. */
  showIncomingBlock?: boolean
}

function LegendDot({ color, opacity }: { color: string; opacity?: number }) {
  return (
    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, opacity: opacity ?? 1, flexShrink: 0, display: 'inline-block' }} />
  )
}

function LegendRow({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      {icon}
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-2)', letterSpacing: '0.04em' }}>{label}</span>
    </div>
  )
}

export function MapLegend({
  style,
  title,
  tiers,
  showAll,
  showOwnLocation,
  showStorefront,
  showDelivery,
  showCluster,
  showProspect,
  showTracking,
  showBlocked,
  showBoth,
  showIncomingBlock,
}: MapLegendProps) {
  const all = showAll ?? false
  return (
    <div style={{
      position: 'absolute', bottom: 12, left: 12, zIndex: 20,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
      padding: '8px 12px', boxShadow: 'var(--card-shadow)',
      display: 'flex', flexDirection: 'column', gap: 6, pointerEvents: 'none',
      ...style,
    }}>
      {title && (
        <div style={{
          fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase',
          letterSpacing: '0.10em', color: 'var(--text-3)', marginBottom: 1,
        }}>{title}</div>
      )}

      {tiers && tiers.map((t, i) => (
        <LegendRow key={i}
          icon={<LegendDot color={t.color} />}
          label={t.label}
        />
      ))}

      {(all || showOwnLocation) && (
        <LegendRow
          icon={
            <div style={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1.5px solid rgba(139,92,246,0.4)', animation: 'cs-ping 2.5s ease-out infinite' }} />
              <div style={{ position: 'absolute', inset: 3, borderRadius: '50%', background: '#8b5cf6' }} />
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 4, height: 4, borderRadius: '50%', background: '#fff' }} />
            </div>
          }
          label="Your locations"
        />
      )}

      {(all || showStorefront) && (
        <LegendRow icon={<LegendDot color={BUSINESS_TYPE_COLORS.storefront} opacity={0.55} />} label="Storefront dispensary" />
      )}

      {(all || showDelivery) && (
        <LegendRow icon={<LegendDot color={BUSINESS_TYPE_COLORS.delivery} opacity={0.55} />} label="Delivery / microbusiness" />
      )}

      {(all || showCluster) && (
        <LegendRow icon={<LegendDot color="#d4537e" />} label="Multiple nearby (cluster)" />
      )}

      {(all || showProspect) && (
        <LegendRow icon={<LegendDot color={BUSINESS_TYPE_COLORS.storefront} opacity={0.55} />} label="Prospect — not yet tracked" />
      )}

      {(all || showTracking) && (
        <LegendRow
          icon={
            <div style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: BUSINESS_TYPE_COLORS.storefront }} />
              <div style={{ position: 'absolute', inset: '50%', transform: 'translate(-50%,-50%)', width: 4, height: 4, borderRadius: '50%', background: TRACK_STATE_COLORS.tracking }} />
            </div>
          }
          label="Selected — tracking"
        />
      )}


    </div>
  )
}
